/**
 * Private Insights · Live Preview Modal
 *
 * Triggered when a client (enterprise OR admin) clicks an in-progress wave card
 * in their PrivateVault. Fetches GET /api/waves/:wid/live and shows:
 *   - PRELIMINARY banner (data is unweighted, not final)
 *   - Headline progress bar  (n_actual / n_target)
 *   - Quota fill heatmap (gender × age × region × SEC)
 *   - Per-question running % (NO cross-tabs by design — those wait until close)
 */
import { useEffect, useState } from "react";
import api from "../lib/api";
import { X, Warning, ChartBar } from "@phosphor-icons/react";

const COLORS = ["#16A34A", "#0055FF", "#F4C430", "#8B5CF6", "#14B8A6", "#EF4444"];

function QuotaHeatBar({ label, actual, target, totalN }) {
  const entries = Object.entries(target || {});
  if (!entries.length) return null;
  return (
    <div>
      <div className="overline text-mango mb-1">{label}</div>
      <div className="space-y-1">
        {entries.map(([k, tgtPct]) => {
          // `actual[k]` is a RAW COUNT (current_counts.<dim>.<key> is $inc'd
          // per submission server-side). The existing pill displays the raw
          // number with a "%" suffix purely as a sparkline-style hint — not
          // a literal percentage. The hover tooltip resolves the ambiguity by
          // showing both the count AND the true share-of-total.
          const rawCount = actual?.[k] || 0;
          const tgtAbs = parseFloat(tgtPct) || 0;
          const sharePct = totalN ? (rawCount / totalN) * 100 : 0;
          const ratio = tgtAbs ? Math.min(200, (rawCount / tgtAbs) * 100) : 0;
          const colour = ratio >= 90 ? "#16A34A" : ratio >= 50 ? "#F4C430" : "#EF4444";
          const tooltip = totalN
            ? `${k}: ${rawCount} responses · ${sharePct.toFixed(1)}% of ${totalN} actual · target ${Math.round(tgtAbs)}%`
            : `${k}: ${rawCount} responses · target ${Math.round(tgtAbs)}%`;
          return (
            <div key={k} className="flex items-center gap-2 text-xs" title={tooltip} data-testid={`quota-bar-${label.toLowerCase()}-${k}`}>
              <div className="w-24 text-neutral-300 truncate">{k}</div>
              <div className="flex-1 bg-neutral-800 h-3 relative overflow-hidden cursor-help">
                <div className="absolute h-full" style={{ width: `${Math.min(100, rawCount)}%`, background: colour }} />
                <div className="absolute h-full border-r-2 border-white opacity-60" style={{ left: `${Math.min(100, tgtAbs)}%`, width: 0 }} />
              </div>
              <div className="w-20 text-right font-mono text-neutral-400">{rawCount} / {Math.round(tgtAbs)}%</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function LivePreviewModal({ waveId, onClose }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!waveId) return;
    let cancelled = false;
    const load = async () => {
      try {
        const r = await api.get(`/waves/${waveId}/live`);
        if (!cancelled) setData(r.data);
      } catch (e) {
        if (!cancelled) setErr(e.response?.data?.detail || "Failed to load live preview");
      }
    };
    load();
    const id = setInterval(load, 15000); // refresh every 15s
    return () => { cancelled = true; clearInterval(id); };
  }, [waveId]);

  if (!waveId) return null;

  return (
    <div className="fixed inset-0 z-[100] bg-black/80 flex items-center justify-center p-4" onClick={onClose} data-testid="live-preview-modal">
      <div className="bg-[#0A0A0A] border-2 border-mango text-white max-w-5xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-neutral-800 sticky top-0 bg-[#0A0A0A]">
          <div>
            <div className="overline text-mango flex items-center gap-2"><ChartBar size={14} /> LIVE PREVIEW · {data?.wave?.status?.toUpperCase() || "—"}</div>
            <h2 className="text-2xl font-black tracking-tight mt-1">{data?.wave?.name || "Loading…"}</h2>
          </div>
          <button onClick={onClose} className="text-neutral-400 hover:text-white" data-testid="live-preview-close"><X size={24} /></button>
        </div>

        {err && <div className="p-6 text-red-400">{err}</div>}

        {data && (
          <div className="p-6 space-y-6">
            {/* Preliminary banner */}
            <div className="bg-yellow-500/10 border border-yellow-500 p-4 flex items-start gap-3" data-testid="preliminary-banner">
              <Warning size={20} className="text-yellow-400 shrink-0 mt-0.5" />
              <div>
                <div className="overline text-yellow-400 font-bold">PRELIMINARY · UNWEIGHTED</div>
                <p className="text-sm text-neutral-300 mt-1">
                  These numbers update every 15 seconds while the wave is in field.
                  Final weighted results — and demographic cross-tabs — will appear once the wave closes and ETL runs.
                </p>
              </div>
            </div>

            {/* Progress headline */}
            <div className="bg-[#111] border border-neutral-800 p-5">
              <div className="flex items-end justify-between mb-2">
                <div>
                  <div className="overline text-mango">FIELDING PROGRESS</div>
                  <div className="font-mono text-4xl font-black mt-1">{data.progress.n_actual} / {data.progress.n_target}</div>
                </div>
                <div className="font-mono text-3xl font-black text-mango">{data.progress.pct_filled}%</div>
              </div>
              <div className="h-2.5 bg-neutral-800 mt-3">
                <div className="h-full bg-mango transition-all" style={{ width: `${Math.min(100, data.progress.pct_filled)}%` }} />
              </div>
              <div className="text-xs text-neutral-500 mt-2 font-mono">
                {data.wave?.start_date ? `Started ${data.wave.start_date}` : "—"} · target close {data.wave?.end_date || "open-ended"}
              </div>
            </div>

            {/* Quota heatmap */}
            <div className="bg-[#111] border border-neutral-800 p-5">
              <div className="overline text-mango mb-3">QUOTA FILL — vs TARGET</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <QuotaHeatBar label="GENDER" actual={data.quota_fill.gender} target={data.quotas_target.gender} totalN={data.progress.n_actual} />
                <QuotaHeatBar label="AGE" actual={data.quota_fill.age} target={data.quotas_target.age} totalN={data.progress.n_actual} />
                <QuotaHeatBar label="REGION" actual={data.quota_fill.region} target={data.quotas_target.region} totalN={data.progress.n_actual} />
                <QuotaHeatBar label="SEC" actual={data.quota_fill.sec} target={data.quotas_target.sec} totalN={data.progress.n_actual} />
              </div>
            </div>

            {/* Per-question running % */}
            <div className="space-y-3">
              <div className="overline text-mango">PER-QUESTION RUNNING SHARE</div>
              {(data.questions || []).map((q) => {
                const optMap = Object.fromEntries((q.options || []).map((o) => [o.key, o.text]));
                const sorted = Object.entries(q.overall_pct || {})
                  .map(([k, v]) => ({ key: k, label: optMap[k] || k, value: v }))
                  .sort((a, b) => b.value - a.value);
                return (
                  <div key={q.id} className="bg-[#111] border border-neutral-800 p-4" data-testid={`live-q-${q.id}`}>
                    <div className="flex items-start justify-between gap-3 mb-3">
                      <h4 className="text-sm font-bold leading-snug">{q.text}</h4>
                      <div className="text-xs font-mono text-neutral-500 shrink-0">n = {q.n_running}</div>
                    </div>
                    {sorted.length === 0 ? (
                      <div className="text-xs text-neutral-500 italic">Awaiting first responses…</div>
                    ) : (
                      <div className="space-y-1.5">
                        {sorted.map((row, i) => {
                          // Back-derive raw count from the running percentage so
                          // the tooltip can show "X of Y · Z%" without an extra
                          // backend trip. Rounding fine for "preliminary" data.
                          const rawCount = q.n_running ? Math.round((row.value / 100) * q.n_running) : 0;
                          const tooltip = `${row.label}: ${rawCount} of ${q.n_running} responses · ${row.value}%`;
                          return (
                            <div key={row.key} className="flex items-center gap-2 text-xs" title={tooltip} data-testid={`live-q-bar-${q.id}-${row.key}`}>
                              <div className="w-44 text-neutral-300 truncate">{row.label}</div>
                              <div className="flex-1 bg-neutral-800 h-3 relative cursor-help">
                                <div className="h-full" style={{ width: `${row.value}%`, background: COLORS[i % COLORS.length] }} />
                              </div>
                              <div className="w-20 text-right font-mono text-neutral-200">{rawCount} · {row.value}%</div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
